import { FEATURES, NONE_ANALYSIS, PRICE_POINTS, STUDY_DESIGN } from '../../data/conjointData';
import { formatPrice } from '../../lib/formatters';
import { usePortfolioPnL } from '../../hooks/useCalculations';
import { Star, Users, DollarSign, Lightbulb, TrendingUp } from 'lucide-react';
import { InfoButton } from '../common/Tooltip';

export function HeadlineFindings() {
  const revPerShowing = PRICE_POINTS.map(p => ({ price: p.price, rps: p.price * p.choiceRate }));
  const bestRPS = revPerShowing.reduce((a, b) => b.rps > a.rps ? b : a);
  const portfolio = usePortfolioPnL();

  const totalUnits = portfolio.totalS2UnitsSplit;
  const weightedNI = totalUnits > 0
    ? portfolio.packages.reduce((s, p) => s + p.netIncomePerUnit * p.s2UnitsSplit, 0) / totalUnits
    : 0;

  const cards = [
    {
      icon: Star,
      label: '#1 Demand Driver',
      value: FEATURES[0].name,
      detail: <><span className="font-mono">+{(FEATURES[0].lift * 100).toFixed(1)}%</span> cold-market demand lift</>,
      color: '#D97706',
      helpKey: 'demandDriver',
    },
    {
      icon: DollarSign,
      label: 'Revenue-Maximizing Price',
      value: formatPrice(bestRPS.price),
      detail: <><span className="font-mono">${Math.round(bestRPS.rps)}</span> revenue per prospect. Based on conjoint demand × price.</>,
      color: '#0D9488',
      helpKey: 'revenueMaximizingPrice',
    },
    {
      icon: Users,
      label: 'Addressable Market',
      value: `${(NONE_ANALYSIS.addressableMarketPercent * 100).toFixed(1)}%`,
      detail: <><span className="font-mono">{NONE_ANALYSIS.addressableMarketCount.toLocaleString()}</span> of <span className="font-mono">{STUDY_DESIGN.totalRespondents.toLocaleString()}</span> show interest. <span className="font-mono">{(NONE_ANALYSIS.alwaysNonePercent * 100).toFixed(1)}%</span> show zero interest at any configuration.</>,
      color: '#2563EB',
      helpKey: 'addressableMarket',
    },
    {
      icon: TrendingUp,
      label: 'Blended Net Income/Unit',
      value: `$${Math.round(weightedNI).toLocaleString()}`,
      detail: <>Weighted average across <span className="font-mono">{portfolio.packages.length}</span> packages (split demand). {weightedNI >= 0 ? 'Positive unit economics.' : 'Negative — every sale loses money before fixed costs.'}</>,
      color: weightedNI >= 0 ? '#059669' : '#DC2626',
      helpKey: 'netIncomePerUnit',
    },
  ];

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 stagger-grid">
        {cards.map(card => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="metric-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: card.color + '12' }}>
                  <Icon size={16} style={{ color: card.color }} />
                </div>
                <span className="text-xs text-secondary uppercase tracking-wider font-medium">{card.label}</span>
                {card.helpKey && <InfoButton helpKey={card.helpKey} size={11} />}
              </div>
              <div className="text-2xl font-bold text-primary font-mono tabular-nums">{card.value}</div>
              <div className="text-sm text-muted mt-1.5 leading-relaxed">{card.detail}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-start gap-3 px-4 py-3 rounded-lg border border-chart-amber/20" style={{ backgroundColor: 'rgba(245, 158, 11, 0.04)' }}>
        <Lightbulb size={16} className="text-chart-amber shrink-0 mt-0.5" />
        <div>
          <span className="text-sm font-semibold text-chart-amber">Margin Opportunity: </span>
          <span className="text-sm text-secondary">
            Payment terms have minimal demand impact — all three options (Pay in Full, Monthly 0% APR, Monthly with Interest) are within <span className="font-mono">1.7 percentage points</span> of each other. Consider eliminating 0% APR financing to improve margins with negligible demand loss.
          </span>
          <InfoButton helpKey="paymentTermsInsight" size={10} />
        </div>
      </div>
    </div>
  );
}
